import {
  NgModule,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgxSpinnerModule } from 'ngx-spinner';
import { FilePondModule, registerPlugin } from 'ngx-filepond';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';
import { IndexComponent } from './index/index.component';
import { AboutComponent } from './about/about.component';
import { CoursesComponent } from './courses/courses.component';
import { UiModule } from '@black/ui';
import { CourseDetailsComponent } from './courses/course-details/course-details.component';
import { CourseEnrollComponent } from './courses/course-enroll/course-enroll.component';
import { AddCourseComponent } from './courses/add-course/add-course.component';
import { CourseIndexComponent } from './courses/course-index/course-index.component';
import { AccordionHeaderComponent } from './courses/add-course/accordion/accordion-header/accordion-header.component';
import { AccordionInputComponent } from './courses/add-course/accordion/accordion-input/accordion-input.component';
import { AccordionResourceComponent } from './courses/add-course/accordion/accordion-resource/accordion-resource.component';
import { AccordionHeaderDefaultComponent } from './courses/add-course/accordion/accordion-header-default/accordion-header-default.component';
import { AccordionContentComponent } from './courses/add-course/accordion/accordion-content/accordion-content.component';
import { UserDashboardComponent } from './dashboard/user-dashboard/user-dashboard.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DashboardHeaderComponent } from './dashboard/dashboard-header/dashboard-header.component';
import { PlaylistComponent } from './courses/playlist/playlist.component';
import { StoreModule } from '@ngrx/store';
import { metaReducers, reducers } from './shared/core/state';
import { EffectsModule } from '@ngrx/effects';

import { CoursesEffects } from './shared/core/state/course';
import { ResourceEffects } from './shared/core/state/resource';
import { UserEffects } from './shared/core/state/users/users.effects';
import { OrderEffects } from './shared/core/state/order/order.effects';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { LoginComponent } from './auth/login/login.component';
import { SignupComponent } from './auth/signup/signup.component';
import { AuthInterceptor } from './shared/util/auth.interceptor';
import { TenantInterceptor } from './shared/util/tenant.interceptor';
import { TenantService } from './shared/services/tenant.service';

export function tenantServiceInitialize(tenantService: TenantService) {
  return () => tenantService.initializeTenant();
}

import { CourseUploadComponent } from './courses/add-course/course-upload/course-upload.component';
import { AddCourseSuccessComponent } from './courses/add-course-success/add-course-success.component';
import { VerifyUserComponent } from './auth/verify-user/verify-user.component';
import { OrderPaymentComponent } from './order/payment/order-payment.component';
import { OrderComponent } from './order/order.component';
import { PaymentSuccessComponent } from './order/payment/payment-success/payment-success.component';
import { MyCoursesComponent } from './courses/my-courses/my-courses.component';
import { InstructorComponent } from './instructor/instructor.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { LiveClassesComponent } from './live-classes/live-classes.component';
import { AddLiveClassComponent } from './live-classes/add-live-class/add-live-class.component';
import { ScheduleFormComponent } from './live-classes/add-live-class/schedule-form/schedule-form.component';
import { LiveClassEffects } from './shared/core/state/live-class/live-class.effects';
import { LiveClassDetailsComponent } from './live-classes/live-class-details/live-class-details.component';
import { SubscriptionComponent } from './subscription/subscription.component';
import { AddSubscriptionComponent } from './live-classes/add-subscription/add-subscription.component';
import { InvoicePaymentComponent } from './subscription/invoice-payment/invoice-payment.component';
import { InvoicePaymentSuccessComponent } from './subscription/invoice-payment-success/invoice-payment-success.component';
import { LearningSpaceComponent } from './learning-space/learning-space.component';
import { MyLiveClassesComponent } from './live-classes/my-live-classes/my-live-classes.component';
import { InvoicesComponent } from './invoices/invoices.component';
import { PasswordResetComponent } from './auth/password-reset/password-reset.component';
import { AddLiveClassSuccessComponent } from './live-classes/add-live-class-success/add-live-class-success.component';
import { ExploreLiveClassesComponent } from './dashboard/explore-live-classes/explore-live-classes.component';

import { UserProfileComponent } from './user-profile/user-profile.component';
import { AddUserProfileComponent } from './user-profile/add-user-profile/add-user-profile.component';
import { TutorsDirectoryComponent } from './tutors-directory/tutors-directory.component';
import { TutorProfileViewComponent } from './tutor-profile-view/tutor-profile-view.component';
import { TutorProfileEditorComponent } from './user-profile/tutor-profile-editor/tutor-profile-editor.component';
import { TutorProfilePageComponent } from './user-profile/tutor-profile-page/tutor-profile-page.component';
import { AccountDeletionComponent } from './user-profile/account-deletion/account-deletion.component';

// import and register filepond file type validation plugin
import * as FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import * as FilePondPluginImageCrop from 'filepond-plugin-image-crop';
import * as FilePondPluginImageResize from 'filepond-plugin-image-resize';
import * as FilePondPluginImageTransform from 'filepond-plugin-image-transform';
import * as FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { ZoomOauthCallbackComponent } from './auth/zoom-oauth-callback/zoom-oauth-callback.component';
import { AccountsComponent } from './auth/accounts/accounts.component';
import { ParentalConsentComponent } from './auth/parental-consent/parental-consent.component';
import { PendingConsentComponent } from './auth/pending-consent/pending-consent.component';
import { DashboardOverviewComponent } from './dashboard/dashboard-overview/dashboard-overview.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { PwaService } from './shared/util/pwa.service';
import { PastMeetingsComponent } from './past-meetings/past-meetings.component';
import { LiveClassContentsComponent } from './learning-space/live-class-contents/live-class-contents.component';
import { StudyMaterialsComponent } from './instructor/study-materials/study-materials.component';
import { ClassAnnouncementsComponent } from './instructor/class-announcements/class-announcements.component';
import { QuizzesComponent } from './instructor/quizzes/quizzes.component';
import { StudentQuizzesComponent } from './learning-space/student-quizzes/student-quizzes.component';
import { InvoicePreviewComponent } from './invoices/invoice-preview/invoice-preview.component';
import { AuthService } from './shared/services/auth.service';
import { TermsComponent } from './terms/terms.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { RefundComponent } from './refund/refund.component';
import { ZoomDocumentationComponent } from './zoom-documentation/zoom-documentation.component';
import { DyteMeetingComponent } from './meetings/third-party/dyte-meeting/dyte-meeting.component';
import { TenantNotFoundComponent } from './shared/component/tenant-not-found/tenant-not-found.component';
import { EarningsComponent } from './dashboard/earnings/earnings.component';
import { BankAccountFormComponent } from './dashboard/earnings/bank-account-form/bank-account-form.component';
import { AppManagementComponent } from './dashboard/app-management/app-management.component';
import { CourseManagementComponent } from './dashboard/course-management/course-management.component';
import { StudentManagementComponent } from './dashboard/student-management/student-management.component';
import { BatchManagementComponent } from './dashboard/batch-management/batch-management.component';
import { MyBatchesComponent } from './dashboard/my-batches/my-batches.component';
import { PaymentManagementComponent } from './dashboard/payment-management/payment-management.component';
import { ImpersonationBannerComponent } from './shared/component/impersonation-banner/impersonation-banner.component';
import { WorkspaceUsageComponent } from './dashboard/workspace-usage/workspace-usage.component';

registerPlugin(
  FilePondPluginImagePreview,
  FilePondPluginImageCrop,
  FilePondPluginImageResize,
  FilePondPluginImageTransform,
  FilePondPluginFileValidateType,
);

const pwaInitialize = (pwaService: PwaService) => {
  pwaService.initPwaPrompt();
};

const authServiceInitialize = (authService: AuthService) => {
  // Force synchronous initialization to ensure token state is available
  // before any route guards are evaluated
  return authService.initializeAuthState();
};
@NgModule({
  declarations: [
    AppComponent,
    AboutComponent,
    CoursesComponent,
    CourseDetailsComponent,
    CourseEnrollComponent,
    AddCourseComponent,
    CourseIndexComponent,
    AccordionHeaderComponent,
    AccordionInputComponent,
    AccordionResourceComponent,
    AccordionHeaderDefaultComponent,
    AccordionContentComponent,
    UserDashboardComponent,
    DashboardComponent,
    DashboardHeaderComponent,
    PlaylistComponent,
    LoginComponent,
    SignupComponent,
    CourseUploadComponent,
    AddCourseSuccessComponent,
    VerifyUserComponent,
    OrderPaymentComponent,
    OrderComponent,
    PaymentSuccessComponent,
    MyCoursesComponent,
    InstructorComponent,
    LiveClassesComponent,
    AddLiveClassComponent,
    LiveClassDetailsComponent,
    SubscriptionComponent,
    AddSubscriptionComponent,
    InvoicePaymentComponent,
    InvoicePaymentSuccessComponent,
    LearningSpaceComponent,
    MyLiveClassesComponent,
    ExploreLiveClassesComponent,
    InvoicesComponent,
    InvoicePreviewComponent,
    PasswordResetComponent,
    UserProfileComponent,
    AddUserProfileComponent,
    AccountDeletionComponent,
    AddLiveClassSuccessComponent,
    ZoomOauthCallbackComponent,
    AccountsComponent,
    ParentalConsentComponent,
    PendingConsentComponent,
    DashboardOverviewComponent,
    PastMeetingsComponent,
    LiveClassContentsComponent,
    StudyMaterialsComponent,
    ClassAnnouncementsComponent,
    QuizzesComponent,
    StudentQuizzesComponent,
    TermsComponent,
    PrivacyComponent,
    RefundComponent,
    ZoomDocumentationComponent,
    ScheduleFormComponent,
    EarningsComponent,
    BankAccountFormComponent,
    AppManagementComponent,
    CourseManagementComponent,
    StudentManagementComponent,
    BatchManagementComponent,
    MyBatchesComponent,
    PaymentManagementComponent,
    ImpersonationBannerComponent,
    WorkspaceUsageComponent,
    TutorsDirectoryComponent,
    TutorProfileViewComponent,
    TutorProfileEditorComponent,
    TutorProfilePageComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    UiModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: false,
      connectInZone: true,
    }),
    EffectsModule.forRoot([
      CoursesEffects,
      UserEffects,
      ResourceEffects,
      OrderEffects,
      LiveClassEffects,
    ]),
    BrowserAnimationsModule,
    NgxSpinnerModule,
    ReactiveFormsModule,
    FormsModule,
    FilePondModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    IndexComponent,
    DyteMeetingComponent,
    TenantNotFoundComponent,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideAppInitializer(() =>
      tenantServiceInitialize(inject(TenantService))(),
    ),
    // Initialize auth service first to ensure token state is available for route guards
    provideAppInitializer(() => authServiceInitialize(inject(AuthService))),
    provideAppInitializer(() => pwaInitialize(inject(PwaService))),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAnimationsAsync(),
    provideClientHydration(withEventReplay()),
  ],
})
export class AppModule {}
